import { apiClient } from './client'
import type {
  ChainOfTitleView,
  CreateChainOfTitleRequest,
  CreateLoanOutRequest,
  CreatePersonnelRequest,
  CreateProductionFYRequest,
  CreateProductionPhaseRequest,
  CreateProductionRequest,
  CreateResidencyCertificationRequest,
  CreateVendorRequest,
  LoanOutView,
  PersonnelView,
  ProductionFYView,
  ProductionPhaseView,
  ProductionView,
  QualifiedSpendLineView,
  ResidencyCertificationView,
  SpendAdjustmentView,
  VendorView,
} from '../types/film'

/**
 * Typed fetch wrappers for `module.film`'s Production/QualifiedSpendLine
 * surface (IB-53 Slice A) — same thin-wrapper convention as `api/accounts.ts`.
 *
 * GUARDED-WRITE CONTRACT (locked — CLAUDE.md §2.1, docs/design/IB-53-film-ui-design.md §3).
 * `qualifyingPct` has **exactly one** write path in this entire file:
 * {@link adjustSpendLine}, which POSTs `{afterPct, comment}` to
 * `/qualified-spend-lines/{id}/adjustments` (the real path — reads use
 * `/spend-lines`, the adjustment sub-resource does not). There is
 * deliberately:
 *   - no `patchSpendLine`/`putSpendLine`/generic `updateSpendLine` export,
 *   - no `method` parameter anywhere in this file (the server defaults it to
 *     `"manual"`; sending anything else 422s — `SpendAdjustmentController`
 *     rejects `computed`/`redistributed`/`unlock`/`ai_accept` from this
 *     endpoint), and
 *   - no function that builds a raw `qualifyingPct`/`inState`/`locked`
 *     request body.
 * `noRawSpendWrite.test.ts` greps this file (and every file under
 * `screens/productions/**`) to keep it that way; `film.test.ts` asserts the
 * exact fetch-call shape below. If you are tempted to add a second way to
 * change `qualifyingPct` here — stop; call {@link adjustSpendLine} instead,
 * or escalate per CLAUDE.md's guardrail note.
 */

/** Single-call Production detail (AC5) — header + phases[] + productionFys[] embedded, no N+1. */
export async function getProduction(id: string, signal?: AbortSignal): Promise<ProductionView> {
  const { data } = await apiClient.get<ProductionView>(`/productions/${id}`, { signal })
  return data
}

/**
 * IB-327 — the "New Production" wizard's ONE entity-shell write:
 * `POST /api/v1/productions` (`ProductionController.create`). Carries no `@GuardedField` at all
 * (Production itself has none — the five guarded figures live on `ProductionFY`, CLAUDE.md §2.1).
 */
export async function createProduction(request: CreateProductionRequest): Promise<ProductionView> {
  const { data } = await apiClient.post<ProductionView>('/productions', request)
  return data
}

/** `POST /api/v1/productions/{id}/phases` — the wizard's optional Phase step(s). */
export async function createProductionPhase(
  productionId: string,
  request: CreateProductionPhaseRequest,
): Promise<ProductionPhaseView> {
  const { data } = await apiClient.post<ProductionPhaseView>(`/productions/${productionId}/phases`, request)
  return data
}

/**
 * `POST /api/v1/productions/{id}/fy` — the wizard's optional ProductionFY-shell step. Deliberately
 * typed with no guarded field in {@link CreateProductionFYRequest} — every created row is a bare
 * shell (backend AC3), never a place to smuggle a `qualifyingPct`/credit figure.
 */
export async function createProductionFY(
  productionId: string,
  request: CreateProductionFYRequest,
): Promise<ProductionFYView> {
  const { data } = await apiClient.post<ProductionFYView>(`/productions/${productionId}/fy`, request)
  return data
}

/**
 * IB-327/IB-326 — `GET /api/v1/productions?legalEntityId=...` (`ProductionController.
 * listByLegalEntity`), a flat, tenant/visibility-scoped list backing the "New Case" wizard's
 * Production picker (film Cases require an existing `productionId`). Deliberately flat, no
 * cursor — a Legal entity's Production count is small and bounded (see the backend javadoc).
 */
export async function listProductionsByLegalEntity(
  legalEntityId: string,
  signal?: AbortSignal,
): Promise<ProductionView[]> {
  const { data } = await apiClient.get<ProductionView[]>(
    `/productions?legalEntityId=${encodeURIComponent(legalEntityId)}`,
    { signal },
  )
  return data
}

/**
 * Flat, unpaginated list scoped by production (C-3 design ruling — the
 * backend exposes no RSQL/sort/cursor here yet; client-side sort/filter over
 * this bounded set is the accepted Slice-A shape, logged as a
 * FR-M23.2-conformance follow-on, not silently assumed).
 */
export async function listSpendLines(
  productionId: string,
  signal?: AbortSignal,
): Promise<QualifiedSpendLineView[]> {
  const { data } = await apiClient.get<QualifiedSpendLineView[]>(`/productions/${productionId}/spend-lines`, {
    signal,
  })
  return data
}

export interface AdjustSpendLineRequest {
  afterPct: number
  comment: string
}

/**
 * THE one write path to `qualifyingPct` (AC8). Deliberately typed with
 * **exactly** `{afterPct, comment}` — no `method`, no `computed`, no
 * `redistributed`, no `unlock`, no `ai_accept`. Returns the server's fresh
 * `QualifiedSpendLineView` — callers must re-render from this response, never
 * apply the submitted value optimistically (AC9, §3.2 step 5).
 */
export async function adjustSpendLine(
  id: string,
  request: AdjustSpendLineRequest,
): Promise<QualifiedSpendLineView> {
  const { data } = await apiClient.post<QualifiedSpendLineView>(
    `/qualified-spend-lines/${id}/adjustments`,
    { afterPct: request.afterPct, comment: request.comment },
  )
  return data
}

export type AuthoritativeRollup = 'simple_avg' | 'cost_weighted'

/**
 * The `authoritativeRollup` switch — an ordinary audited mutation (NOT
 * `@GuardedField`, no `MandatoryCommentModal`; design §3.1 table). Distinct
 * from every guarded write above: no comment, no clamp, no adjustment ledger.
 */
export async function switchRollup(
  productionId: string,
  fiscalYearId: string,
  authoritativeRollup: AuthoritativeRollup,
  ifMatchVersion: number,
): Promise<ProductionFYView> {
  const { data } = await apiClient.patch<ProductionFYView>(
    `/productions/${productionId}/fy/${fiscalYearId}`,
    { authoritativeRollup },
    { ifMatchVersion },
  )
  return data
}

/**
 * The adjustment-history read (IB-155/IB-53b, ADR-0041) — a **read-only** GET wrapper. Does not
 * touch {@link adjustSpendLine} or any write path; adds no `method` parameter, no raw
 * `qualifyingPct`/`inState`/`locked` request body anywhere in this file. Newest-first, matching
 * the backend's own ordering — this wrapper does not re-sort.
 */
export async function listSpendLineAdjustments(
  id: string,
  signal?: AbortSignal,
): Promise<SpendAdjustmentView[]> {
  const { data } = await apiClient.get<SpendAdjustmentView[]>(
    `/qualified-spend-lines/${id}/adjustments`,
    { signal },
  )
  return data
}

// ---------------------------------------------------------------------------
// Personnel / LoanOut / ResidencyCertification / Vendor / ChainOfTitle
// (IB-155 Slice B) — CRUD-minus-guarded wrappers over already-merged backend
// endpoints (docs/design/IB-155-film-ui-sliceB-design.md §3). `Personnel.resident`
// has no write channel at all (deliberately no update wrapper here); `Vendor.inState`
// has no PATCH (settable only at create, no update wrapper either). Every `verified`/
// `qualified` PATCH below is an ordinary audited mutation — never a MandatoryCommentModal.
// ---------------------------------------------------------------------------

export async function listPersonnel(productionId: string, signal?: AbortSignal): Promise<PersonnelView[]> {
  const { data } = await apiClient.get<PersonnelView[]>(`/productions/${productionId}/personnel`, { signal })
  return data
}

export async function createPersonnel(
  productionId: string,
  request: CreatePersonnelRequest,
): Promise<PersonnelView> {
  const { data } = await apiClient.post<PersonnelView>(`/productions/${productionId}/personnel`, request)
  return data
}

export async function listLoanOuts(signal?: AbortSignal): Promise<LoanOutView[]> {
  const { data } = await apiClient.get<LoanOutView[]>('/loan-outs', { signal })
  return data
}

export async function createLoanOut(request: CreateLoanOutRequest): Promise<LoanOutView> {
  const { data } = await apiClient.post<LoanOutView>('/loan-outs', request)
  return data
}

/** The `verified` flip (AC11) — ordinary mutation, no comment modal. */
export async function updateLoanOutVerified(
  id: string,
  verified: boolean,
  ifMatchVersion: number,
): Promise<LoanOutView> {
  const { data } = await apiClient.patch<LoanOutView>(`/loan-outs/${id}`, { verified }, { ifMatchVersion })
  return data
}

export async function listResidencyCertifications(
  personnelId: string,
  signal?: AbortSignal,
): Promise<ResidencyCertificationView[]> {
  const { data } = await apiClient.get<ResidencyCertificationView[]>(
    `/personnel/${personnelId}/residency-certifications`,
    { signal },
  )
  return data
}

export async function createResidencyCertification(
  personnelId: string,
  request: CreateResidencyCertificationRequest,
): Promise<ResidencyCertificationView> {
  const { data } = await apiClient.post<ResidencyCertificationView>(
    `/personnel/${personnelId}/residency-certifications`,
    request,
  )
  return data
}

/** The `verified` flip (AC13) — ordinary mutation, no comment modal. */
export async function updateResidencyCertificationVerified(
  personnelId: string,
  id: string,
  verified: boolean,
  ifMatchVersion: number,
): Promise<ResidencyCertificationView> {
  const { data } = await apiClient.patch<ResidencyCertificationView>(
    `/personnel/${personnelId}/residency-certifications/${id}`,
    { verified },
    { ifMatchVersion },
  )
  return data
}

export async function listVendors(signal?: AbortSignal): Promise<VendorView[]> {
  const { data } = await apiClient.get<VendorView[]>('/vendors', { signal })
  return data
}

/** Both `inState` and `qualified` are required up front (AC17) — no later `inState` edit path exists. */
export async function createVendor(request: CreateVendorRequest): Promise<VendorView> {
  const { data } = await apiClient.post<VendorView>('/vendors', request)
  return data
}

/** The `qualified` flip (AC16) — ordinary mutation, no comment modal. `inState` has no PATCH. */
export async function updateVendorQualified(
  id: string,
  qualified: boolean,
  ifMatchVersion: number,
): Promise<VendorView> {
  const { data } = await apiClient.patch<VendorView>(`/vendors/${id}`, { qualified }, { ifMatchVersion })
  return data
}

export async function listChainOfTitle(productionId: string, signal?: AbortSignal): Promise<ChainOfTitleView[]> {
  const { data } = await apiClient.get<ChainOfTitleView[]>(`/productions/${productionId}/chain-of-title`, {
    signal,
  })
  return data
}

export async function createChainOfTitle(
  productionId: string,
  request: CreateChainOfTitleRequest,
): Promise<ChainOfTitleView> {
  const { data } = await apiClient.post<ChainOfTitleView>(
    `/productions/${productionId}/chain-of-title`,
    request,
  )
  return data
}

/** The `verified` flip (AC18) — ordinary mutation, no comment modal. */
export async function updateChainOfTitleVerified(
  productionId: string,
  id: string,
  verified: boolean,
  ifMatchVersion: number,
): Promise<ChainOfTitleView> {
  const { data } = await apiClient.patch<ChainOfTitleView>(
    `/productions/${productionId}/chain-of-title/${id}`,
    { verified },
    { ifMatchVersion },
  )
  return data
}
